// Print jobs: everything one print run produced (one or more pages made of one or
// more transfers). The device marks job boundaries in the stream (FE 0A: boot, C64
// reset, long press of the button); this module cuts the event stream into jobs.
//
// Rules (see "Print jobs" in docs/wifi-printing.md):
//  - a job only exists once something is printed on the paper (ink: a character that
//    is not a blank, a bit image column with a dot). A boundary followed by another
//    boundary, or by nothing but spaces, line feeds and page breaks, creates no job
//    and the events in between are dropped;
//  - a boundary closes the open job; there is no other "job finished" signal;
//  - a USB reconnect (the stream hello) is not a boundary, a page break stays in the job.
import { StreamParser } from './protocol.js';
import { Mps803Decoder } from './mps803.js';

// Why a job began (the device's FE 0A reasons, plus two of our own).
export const WHY = { START: 0, BOOT: 1, RESET: 2, BUTTON: 3, MANUAL: 4 };
export const WHY_TEXT = ['start', 'device boot', 'C64 reset', 'button', 'new job'];

// ---- events: what a job is made of ----
//   {t: 'listen', sa} {t: 'data', bytes} {t: 'eoi'} {t: 'unlisten'} {t: 'pagebreak'}

export function eventWireSize(ev) {
  switch (ev.t) {
    case 'data': {
      let n = ev.bytes.length;
      for (const b of ev.bytes) if (b === 0xfe) n++; // escaped
      return n;
    }
    case 'listen': return 3;
    default: return 2; // eoi, unlisten, pagebreak
  }
}

// The events as wire bytes (the format of stream.h), used to store a job.
export function encodeEvents(events) {
  const out = [];
  for (const ev of events) {
    if (ev.t === 'data') {
      for (const b of ev.bytes) {
        out.push(b);
        if (b === 0xfe) out.push(0x00);
      }
    } else if (ev.t === 'listen') out.push(0xfe, 0x01, ev.sa);
    else if (ev.t === 'unlisten') out.push(0xfe, 0x02);
    else if (ev.t === 'eoi') out.push(0xfe, 0x03);
    else if (ev.t === 'pagebreak') out.push(0xfe, 0x05);
  }
  return Uint8Array.from(out);
}

export function decodeEvents(bytes) {
  const events = [];
  const parser = new StreamParser({
    onListen: (sa) => events.push({ t: 'listen', sa }),
    onData: (d) => events.push({ t: 'data', bytes: d.slice() }),
    onEoi: () => events.push({ t: 'eoi' }),
    onUnlisten: () => events.push({ t: 'unlisten' }),
    onPageBreak: () => events.push({ t: 'pagebreak' }),
  });
  parser.push(bytes);
  return events;
}

// Calls the matching handler for each event, like the stream parser would.
export function replayEvents(events, h) {
  for (const ev of events) applyEvent(ev, h);
}
export function applyEvent(ev, h) {
  if (ev.t === 'data') h.onData?.(ev.bytes);
  else if (ev.t === 'listen') h.onListen?.(ev.sa);
  else if (ev.t === 'eoi') h.onEoi?.();
  else if (ev.t === 'unlisten') h.onUnlisten?.();
  else if (ev.t === 'pagebreak') h.onPageBreak?.();
}

// Does this decoded item put ink on paper? A space (0x20, or 0xA0 shifted) does not,
// unless it is printed in reverse video (a solid block); a bit image column only with a dot.
function itemHasInk(item) {
  if (item.kind === 'bits') return item.value !== 0;
  return item.reverse || (item.code !== 0x20 && item.code !== 0xa0);
}

// Decides whether a stream of events puts ink on paper: feed it the events of a job one by one.
// It runs the printer decoder, so reverse video, bit images and the character set are respected.
export class InkProbe {
  constructor() {
    this.ink = false;
    this.decoder = new Mps803Decoder({ onLine: (l) => l.items.some(itemHasInk) && (this.ink = true) });
  }

  // True once ink has been seen (it stays true).
  feed(ev) {
    const d = this.decoder;
    if (ev.t === 'listen') d.listen(ev.sa);
    else if (ev.t === 'unlisten') d.unlisten();
    else if (ev.t === 'data') d.data(ev.bytes);
    return this.ink || d.items.some(itemHasInk);
  }
}

// Incremental stream bytes -> content events (the parser keeps its state between pushes, so the
// byte chunks may be cut anywhere). Boundaries, hello, responses and so on are not content.
export class EventParser {
  constructor() {
    this.out = [];
    this.parser = new StreamParser({
      onListen: (sa) => this.out.push({ t: 'listen', sa }),
      onData: (d) => this.out.push({ t: 'data', bytes: d.slice() }),
      onEoi: () => this.out.push({ t: 'eoi' }),
      onUnlisten: () => this.out.push({ t: 'unlisten' }),
      onPageBreak: () => this.out.push({ t: 'pagebreak' }),
    });
  }

  push(bytes) {
    this.out = [];
    this.parser.push(bytes);
    return this.out;
  }
}

export { itemHasInk };

function measure(events) {
  let dataBytes = 0;
  let wireBytes = 0;
  for (const ev of events) {
    wireBytes += eventWireSize(ev);
    if (ev.t === 'data') dataBytes += ev.bytes.length;
  }
  return { dataBytes, wireBytes };
}

// A job rebuilt from its stored form (see jobstore.js).
export function jobFromRecord(rec) {
  const events = decodeEvents(rec.wire);
  return {
    id: rec.id,
    why: rec.why,
    endWhy: rec.endWhy,
    interrupted: rec.interrupted,
    name: rec.name,
    startedAt: new Date(rec.startedAt),
    lastDataAt: new Date(rec.lastDataAt),
    events,
    ...measure(events),
  };
}

export function jobToRecord(job) {
  return {
    id: job.id,
    why: job.why,
    endWhy: job.endWhy,
    interrupted: job.interrupted,
    name: job.name,
    startedAt: job.startedAt.getTime(),
    lastDataAt: job.lastDataAt.getTime(),
    wire: encodeEvents(job.events),
  };
}

export class JobBook {
  // onStart(job): a job was created (its first data byte arrived); onEvent(job, ev): an
  // event was added to a started job; onChange(job): anything about the job changed
  // (to be stored); onRemove(jobs): jobs were removed by trim().
  constructor({ onStart, onEvent, onChange, onRemove, now = () => new Date() } = {}) {
    this.cb = { onStart, onEvent, onChange, onRemove };
    this.now = now;
    this.jobs = []; // oldest first, ids ascending
    this.current = null; // the open job (started, not closed), or null
    this.pending = null; // events since the last boundary, waiting for the first ink
    this.listening = false; // between LISTEN and UNLISTEN of the stream so far
    this.listenSa = 0;
    this.probe = null; // InkProbe over the pending events
    this.nextId = 1;
    this.beginPending(null);
  }

  // Starts waiting for the first ink of a new job. A job that begins while the
  // device is listening (the long press in the middle of a transfer) starts with
  // a LISTEN of its own, so that its data is decoded like data of a transfer.
  beginPending(why) {
    this.pending = { why, events: [] };
    this.probe = new InkProbe();
    if (this.listening) {
      const ev = { t: 'listen', sa: this.listenSa };
      this.pending.events.push(ev);
      this.probe.feed(ev);
    }
  }

  // Parser handlers (use with tee()).
  handlers() {
    return {
      onListen: (sa) => this.record({ t: 'listen', sa }),
      onData: (bytes) => this.record({ t: 'data', bytes: bytes.slice() }),
      onEoi: () => this.record({ t: 'eoi' }),
      onUnlisten: () => this.record({ t: 'unlisten' }),
      onPageBreak: () => this.record({ t: 'pagebreak' }),
      // Old firmware had no FE 0A: a reset still starts a new job. With new
      // firmware the FE 0A that follows coalesces with this boundary.
      onReset: () => this.boundary(WHY.RESET, false),
      onNewJob: (why, cut) => this.boundary(why, cut),
    };
  }

  // Adds an event to a started job (also a closed one: a manual page break) and tells the page.
  addEvent(job, ev) {
    this.add(job, ev);
    this.cb.onEvent?.(job, ev);
  }

  record(ev) {
    if (ev.t === 'listen') {
      this.listening = true;
      this.listenSa = ev.sa;
    } else if (ev.t === 'unlisten') {
      this.listening = false;
    }
    const job = this.current;
    if (job) {
      this.addEvent(job, ev);
      return;
    }
    this.pending.events.push(ev);
    if (this.probe.feed(ev)) this.start();
  }

  add(job, ev) {
    job.events.push(ev);
    job.wireBytes += eventWireSize(ev);
    if (ev.t === 'data') {
      job.dataBytes += ev.bytes.length;
      job.lastDataAt = this.now();
    }
    this.cb.onChange?.(job);
  }

  start() {
    const events = this.pending.events;
    const at = this.now();
    const job = {
      id: this.nextId++,
      why: this.pending.why ?? WHY.START,
      endWhy: null,
      interrupted: false,
      name: null,
      startedAt: at,
      lastDataAt: at,
      events,
      ...measure(events),
    };
    this.pending = null;
    this.jobs.push(job);
    this.current = job;
    this.cb.onStart?.(job);
    this.cb.onChange?.(job);
  }

  // A job boundary from the device (or manual). `cut`: the previous job ended in the middle of a transfer.
  boundary(why, cut = false) {
    if (why === WHY.RESET || why === WHY.BOOT) this.listening = false;
    if (cut) {
      const last = this.jobs[this.jobs.length - 1];
      if (last && !last.interrupted) {
        last.interrupted = true;
        this.cb.onChange?.(last);
      }
    }
    const job = this.current;
    if (job) {
      job.endWhy = why;
      this.current = null;
      this.beginPending(why);
      this.cb.onChange?.(job);
    } else {
      // Nothing was printed since the previous boundary: no job, drop what was buffered.
      this.beginPending(this.pending.why ?? why);
    }
  }

  // The user ends the open job ("New job"): the next data byte starts another one.
  closeCurrent() {
    if (this.current) this.boundary(WHY.MANUAL, false);
  }

  // Removes jobs by id; if the open job is among them, the next data byte starts a new one.
  remove(ids) {
    const set = new Set(ids);
    const gone = this.jobs.filter((j) => set.has(j.id));
    if (gone.length === 0) return gone;
    this.jobs = this.jobs.filter((j) => !set.has(j.id));
    if (this.current && set.has(this.current.id)) {
      this.current = null;
      this.beginPending(WHY.MANUAL);
    }
    return gone;
  }

  clear() {
    return this.remove(this.jobs.map((j) => j.id));
  }

  // Adds a stored (closed) job, e.g. loaded from IndexedDB. Must be called before the first live data.
  adopt(job) {
    this.jobs.push(job);
    this.jobs.sort((a, b) => a.id - b.id);
    this.nextId = Math.max(this.nextId, job.id + 1);
  }

  // Drops the oldest closed jobs while there are more than maxJobs or more than maxBytes of wire data.
  trim(maxJobs, maxBytes) {
    const removed = [];
    let total = this.jobs.reduce((n, j) => n + j.wireBytes, 0);
    for (const j of this.jobs) {
      if (this.jobs.length - removed.length <= maxJobs && total <= maxBytes) break;
      if (j === this.current) continue;
      removed.push(j);
      total -= j.wireBytes;
    }
    if (removed.length) {
      this.remove(removed.map((j) => j.id));
      this.cb.onRemove?.(removed);
    }
    return removed;
  }
}
